import * as React from "react";
import { Navigate } from "react-router-dom";
import AuthService from "@/services/AuthService";

/**
 * VelaroAdminRoute — gates a route to Velaro-employee identities only (site 1032).
 *
 * This is an IDENTITY gate (per-user), NOT a subscription/feature gate (per-tenant).
 * Use it to wrap routes that expose Velaro-internal tooling — our own billing,
 * QuickBooks ledger mapping, Stripe (our payment processor) settings, superadmin
 * pages, dev tools, etc. Customers must never reach these pages even by typing the
 * URL directly.
 *
 * Hiding the sidebar entry is not sufficient — the route still resolves and renders
 * if a customer visits the URL. This wrapper hard-redirects non-Velaro identities.
 *
 * Backend endpoints these pages call MUST also enforce role checks —
 * frontend gates are bypassable. This wrapper is defense-in-depth, not the only defense.
 *
 * Role tiers:
 *   isVelaroAdmin   — full internal access (all billing + superadmin tooling)
 *   isVelaroBilling — billing only (invoices, contracts, Stripe, usage packs)
 *   isVelaroSupport — operational tools only (no billing)
 *
 * Usage:
 *   <VelaroAdminRoute>          — requires isVelaroAdmin (superadmin pages)
 *   <VelaroAdminRoute billing>  — accepts isVelaroAdmin OR isVelaroBilling (billing pages)
 */
export function VelaroAdminRoute({
  children,
  billing = false,
}: {
  children: React.ReactNode;
  billing?: boolean;
}) {
  const profile = AuthService.profile;
  const isVelaroAdmin = profile?.isVelaroAdmin === true;
  const isVelaroBilling = profile?.isVelaroBilling === true;
  const isVelaroEmail = profile?.userName?.endsWith("@velaro.com") === true;

  const hasAccess = billing
    ? (isVelaroAdmin || isVelaroBilling) && isVelaroEmail
    : isVelaroAdmin && isVelaroEmail;

  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export default VelaroAdminRoute;
